import React, { useState, useEffect } from "react";
import { TextInputValidationRules } from "types";

interface TextInputsProps {
  label: string;
  value: string;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  placeholder?: string;
  rules?: TextInputValidationRules;
  error?: string; // external error support
}

const TextInputs: React.FC<TextInputsProps> = ({
  label,
  value,
  name,
  onChange,
  onBlur,
  placeholder = "John",
  rules = {},
  error: externalError,
}) => {
  const [error, setError] = useState<string | null>(null);

  const validate = (val: string): string | null => {
    if (rules.required) {
      const msg =
        typeof rules.required === "string"
          ? rules.required
          : `${label} is required`;
      if (!val.trim()) return msg;
    }

    if (rules.minLength && val.length < rules.minLength.value) {
      return (
        rules.minLength.message ||
        `${label} must be at least ${rules.minLength.value} characters`
      );
    }

    if (rules.maxLength && val.length > rules.maxLength.value) {
      return (
        rules.maxLength.message ||
        `${label} must be at most ${rules.maxLength.value} characters`
      );
    }

    if (rules.pattern && !rules.pattern.value.test(val)) {
      return rules.pattern.message || `${label} format is invalid`;
    }

    if (rules.validate) {
      const result = rules.validate(val);
      if (result !== true) {
        return typeof result === "string" ? result : `${label} is invalid`;
      }
    }

    return null;
  };

  useEffect(() => {
    setError(validate(value));
  }, [value]);

  const displayError = externalError || error;

  return (
    <div>
      <label
        htmlFor={name}
        className="block mb-2 text-sm font-medium text-[#747171]"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={!!displayError}
        aria-describedby={displayError ? `${name}-error` : undefined}
        className={`text-sm rounded-lg block w-full p-2.5 text-gray-900 focus:outline-none
                    ${displayError ? "border-red-500" : ""}`}
        style={{ backgroundColor: "rgb(249 250 251 / var(--tw-bg-opacity, 1))", border: "none" }}
      />
      {displayError && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-500"
        >
          {displayError}
        </p>
      )}
    </div>
  );
};

export default TextInputs;

